// Cloud Sync — pure decisions for per-VOD state (no Angular, no Firestore).
// sync.service.ts / twitch-chat-state.service.ts wire them to IDB + Firestore.
//
// Version model: every cloud write of `state/main` carries a fresh `rev` token.
// A local copy remembers the rev it is based on (`SavedState.syncRev`) and whether
// it has edits the cloud hasn't confirmed yet (`syncDirty`). Copies saved before
// this scheme have no syncRev ("legacy") — for those we can't tell stale from ahead,
// so `workScore` decides (see decideOnOpen).
import type { SavedState } from '../services/twitch-chat-state.service';

/** syncRev of a copy created on this device from scratch (fresh chat download):
 *  its first push may only create the cloud doc, never replace an existing one. */
export const NEW_DOC_REV = '#new';

export interface RemoteState {
  state: SavedState;
  rev: string; // '' = legacy cloud doc written before revs existed
  updatedAtMs: number | null;
}

export type OpenDecision =
  | { kind: 'none' }
  | { kind: 'local'; push: boolean; baseRev: string | undefined }
  | { kind: 'remote'; baseRev: string }
  | { kind: 'conflict'; baseRev: string };

/**
 * What the user did to the VOD — pins, deletions, promotions, merges, clip bounds,
 * AI runs, threshold/filters. Ignores chat-derived numbers (messageCount, bins) and
 * AI wording, so two devices that downloaded the chat separately compare equal.
 */
export function workFingerprint(s: SavedState): string {
  const hl = (s.highlights || [])
    .map(h => [
      Math.round(h.startTime * 10) / 10,
      h.source ?? 'peak',
      h.isPinned ? 1 : 0,
      h.isDeleted ? 1 : 0,
      h.isPromoted ? 1 : 0,
      h.isMerged ? 1 : 0,
      h.isAnalyzed ? 1 : 0,
      h.isStartTimeManuallySet ? 1 : 0,
      Math.round(h.clipStartTime ?? 0),
      Math.round(h.clipEndTime ?? 0),
    ] as const)
    .sort((a, b) => a[0] - b[0] || String(a[1]).localeCompare(String(b[1])));
  // Explicit fields, not JSON of the object: Firestore returns map keys in its own
  // order, so the same filters would otherwise fingerprint differently.
  const f = s.filterSettings;
  const filters = f ? [!!f.hideSubs, [...(f.keywords || [])].sort(), [...(f.ignoredUsers || [])].sort()] : null;
  return JSON.stringify([hl, s.peakMessageThreshold ?? null, filters]);
}

/**
 * Changes worth a cloud write: user work + AI/search text. Chat-derived numbers
 * (bins, messageCount, clip view counts) are left out — they change on every chat
 * re-download / clip refresh and are rebuilt on each device anyway; they ride
 * along with the next real edit.
 */
export function syncSignature(s: SavedState): string {
  const text = (s.highlights || []).map(h => [h.startTime, h.description, h.sentiment, h.isPinned ? 1 : 0]);
  return workFingerprint(s) + '|' + JSON.stringify([text, s.rewindOffsetSeconds ?? null]);
}

/** Rough amount of user work in a copy — tie-breaker for legacy copies only. */
export function workScore(s: SavedState): number {
  let n = 0;
  for (const h of s.highlights || []) {
    if (h.isPinned) n++;
    if (h.isDeleted) n++;
    if (h.isPromoted) n++;
    if (h.isMerged) n++;
    if (h.isStartTimeManuallySet) n++;
    if (h.isAnalyzed) n++;
    // AI-deep / search / transcript results exist only because the user ran them
    if (h.source && h.source !== 'peak' && h.source !== 'stream-marker' && h.source !== 'user-clip') n++;
  }
  return n;
}

function hasContent(s: SavedState): boolean {
  return (s.highlights?.length ?? 0) > 0 || (s.activityBins?.length ?? 0) > 0;
}

/**
 * Which copy to show when a VOD is opened (or re-checked) on this device.
 * `remote === null` = no cloud doc. Callers handle "cloud unreachable" themselves
 * (keep local, decide again later).
 */
export function decideOnOpen(local: SavedState | null | undefined, remote: RemoteState | null): OpenDecision {
  if (!local && !remote) return { kind: 'none' };
  if (!local) return { kind: 'remote', baseRev: remote!.rev };
  if (!remote) {
    return { kind: 'local', push: hasContent(local), baseRev: local.syncRev === undefined ? NEW_DOC_REV : local.syncRev };
  }

  if (local.syncRev !== undefined) {
    if (local.syncRev === remote.rev) return { kind: 'local', push: !!local.syncDirty, baseRev: remote.rev };
    // Cloud moved on since our base.
    if (!local.syncDirty) return { kind: 'remote', baseRev: remote.rev };
    // Both sides changed. Same work on both → nothing to choose.
    if (workFingerprint(local) === workFingerprint(remote.state)) return { kind: 'local', push: false, baseRev: remote.rev };
    return { kind: 'conflict', baseRev: remote.rev };
  }

  // Legacy local copy — unknown whether it is stale or ahead of the cloud.
  if (workFingerprint(local) === workFingerprint(remote.state)) return { kind: 'local', push: false, baseRev: remote.rev };
  if (workScore(local) > workScore(remote.state)) return { kind: 'local', push: true, baseRev: remote.rev };
  return { kind: 'remote', baseRev: remote.rev };
}

/**
 * Push guard, evaluated inside the Firestore transaction against the doc as it is
 * right now. `baseRev` = the cloud rev the outgoing copy is based on.
 */
export function canOverwrite(baseRev: string | undefined, remote: RemoteState | null, outgoing: SavedState): boolean {
  if (!remote) return true;
  if (baseRev === NEW_DOC_REV) return false;
  // Never reconciled (legacy copy, cloud was unreachable on open): only overwrite
  // when no user work would be lost.
  if (baseRev === undefined) return workFingerprint(remote.state) === workFingerprint(outgoing);
  return remote.rev === baseRev;
}

/**
 * Cloud copies carry no chat (and bins without per-bin messages). When the cloud
 * copy wins on a device that has the chat, keep the local chat + bins so the user
 * doesn't have to re-download it. Bins come with the filters they were built from.
 */
export function withLocalChat(remote: SavedState, local: SavedState | null | undefined): SavedState {
  if (!local?.chatComments?.length) return remote;
  const out: SavedState = { ...remote, chatComments: local.chatComments };
  if (local.activityBins?.length) {
    out.activityBins = local.activityBins;
    out.maxActivity = local.maxActivity;
    out.filterSettings = local.filterSettings;
  }
  out.chatDownloadComplete = local.chatDownloadComplete;
  out.chatDownloadCursor = local.chatDownloadCursor;
  out.isLive = local.isLive;
  out.liveStreamId = local.liveStreamId;
  return out;
}

export function newRev(): string {
  return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
}
